import { getPrimaryImageFileId } from 'src/modules/shared/utils/catalog/property-media.util';
import { getPropertyClassificationLabel } from './property-classification.util';
import React from 'react';
import { formatDistanceToNow, type Locale } from 'date-fns';
import { PiArrowsClockwise, PiPauseCircle } from 'react-icons/pi';
import { PropertyOperation } from 'src/api/generated';
import type { CatalogProperty } from 'src/api/generated/models/CatalogProperty';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import type { TagDropdownOption } from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { formatCurrency } from 'src/modules/shared/utils/formatting/currency.util';
import type {
  PropertyCardViewModel,
  PropertyMetricView,
  SyncProviderId,
} from '../types/properties-list.types';

type TranslateFn = (
  key: TranslationKey,
  params?: Record<string, string>
) => string;

const operationLabelKeyByType: Record<PropertyOperation, TranslationKey> = {
  [PropertyOperation.SALE]: 'catalog.properties.operation.SALE',
  [PropertyOperation.RENT]: 'catalog.properties.operation.RENT',
  [PropertyOperation.SALE_AND_RENT]:
    'catalog.properties.operation.SALE_AND_RENT',
  [PropertyOperation.TEMPORARY_RENT]:
    'catalog.properties.operation.TEMPORARY_RENT',
};

const syncProviderLabelKeyById: Record<SyncProviderId, TranslationKey> = {
  meli: 'catalog.properties.list.provider.meli',
  wasi: 'catalog.properties.list.provider.wasi',
  toppropiedades: 'catalog.properties.list.provider.toppropiedades',
  kiteprop: 'catalog.properties.list.provider.kiteprop',
};

const formatPriceValue = (
  amount?: string | null,
  currency?: string | null
): string | null => {
  if (!amount || !currency) {
    return null;
  }

  return formatCurrency(amount, currency, 0);
};

const buildAddress = (property: CatalogProperty): string | null => {
  if (property.addressDisplay?.trim()) {
    return property.addressDisplay.trim();
  }

  const value = [property.streetName, property.streetNumber, property.unit]
    .filter(
      (part): part is string =>
        typeof part === 'string' && part.trim().length > 0
    )
    .join(' ');

  return value.length > 0 ? value : null;
};

const formatMetricNumber = (value?: number | string | null): string | null => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'number') {
    return new Intl.NumberFormat('es-CL').format(value);
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getSyncProviderId = (
  sourceSystem?: string | null
): SyncProviderId | null => {
  if (sourceSystem === 'meli') return 'meli';
  if (sourceSystem === 'wasi') return 'wasi';
  if (sourceSystem === 'toppropiedades') return 'toppropiedades';
  if (sourceSystem === 'kiteprop') return 'kiteprop';
  return null;
};

const buildGoogleMapsUrl = (property: CatalogProperty): string | null => {
  const address = buildAddress(property);
  const broaderLocation = [
    property.locality,
    property.region,
    property.neighborhood,
  ]
    .filter(
      (part): part is string =>
        typeof part === 'string' && part.trim().length > 0
    )
    .join(', ');

  const query = [address, broaderLocation]
    .filter(
      (part): part is string =>
        typeof part === 'string' && part.trim().length > 0
    )
    .join(', ');

  if (!query) {
    return null;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`;
};

const buildLocationSummary = (property: CatalogProperty): string =>
  [property.locality, property.neighborhood]
    .filter(
      (value): value is string =>
        typeof value === 'string' && value.trim().length > 0
    )
    .join(' · ');

const buildPropertyMetrics = (
  property: CatalogProperty,
  t: TranslateFn
): PropertyMetricView[] => [
  {
    key: 'bedrooms',
    label: t('catalog.properties.list.metric.bedrooms'),
    value:
      property.bedrooms != null
        ? `${formatMetricNumber(property.bedrooms)}D`
        : '-',
    isMissing: property.bedrooms == null,
  },
  {
    key: 'bathrooms',
    label: t('catalog.properties.list.metric.bathrooms'),
    value:
      property.bathrooms != null
        ? `${formatMetricNumber(property.bathrooms)}B`
        : '-',
    isMissing: property.bathrooms == null,
  },
  {
    key: 'area',
    label: property.areaUsable
      ? t('catalog.properties.list.metric.areaUsable')
      : t('catalog.properties.list.metric.areaTotal'),
    value: property.areaUsable
      ? `${formatMetricNumber(property.areaUsable)} m²`
      : property.areaTotal
        ? `${formatMetricNumber(property.areaTotal)} m²`
        : '-',
    isMissing: !property.areaUsable && !property.areaTotal,
  },
];

const buildStatusPills = (
  property: CatalogProperty,
  syncProviderId: SyncProviderId | null,
  t: TranslateFn
): React.ReactNode[] =>
  [
    property.isActive ? (
      <StatusPill
        key="active"
        label={t('catalog.properties.list.status.active')}
        variant="success"
      />
    ) : (
      <StatusPill
        key="inactive"
        label={t('catalog.properties.list.status.inactive')}
        variant="neutral"
      />
    ),
    property.operation ? (
      <StatusPill
        key="operation"
        label={t(operationLabelKeyByType[property.operation])}
        variant="primary"
        appearance="outline"
      />
    ) : null,
    property.propertyType ? (
      <StatusPill
        key="type"
        label={getPropertyClassificationLabel(
          property.propertyType,
          property.propertySubtype,
          t
        )}
        variant="secondary"
        appearance="outline"
      />
    ) : null,
    syncProviderId ? (
      <StatusPill
        key={`source-${syncProviderId}`}
        label={t(syncProviderLabelKeyById[syncProviderId])}
        icon={<PiArrowsClockwise className="h-3.5 w-3.5" />}
        variant="warning"
        appearance="soft"
      />
    ) : property.sourceSystem ? (
      <StatusPill
        key="source-external"
        label={t('catalog.properties.list.badge.imported', {
          source: property.sourceSystem,
        })}
        variant="secondary"
        appearance="soft"
      />
    ) : null,
  ].filter(Boolean);

const buildWarnings = (
  property: CatalogProperty,
  hasPrice: boolean,
  hasLocation: boolean,
  t: TranslateFn
): string[] =>
  [
    !hasPrice ? t('catalog.properties.list.warning.noPrice') : null,
    !hasLocation ? t('catalog.properties.list.warning.noLocation') : null,
    property.userId == null
      ? t('catalog.properties.list.warning.unassigned')
      : null,
  ].filter((value): value is string => value != null);

const buildUserActions = (
  onDeactivate: ((property: CatalogProperty) => void) | undefined,
  property: CatalogProperty,
  t: TranslateFn
): TagDropdownOption[] => {
  if (!onDeactivate || !property.isActive) {
    return [];
  }

  return [
    {
      id: 'deactivate',
      content: (
        <Text size="small" color="danger">
          {t('catalog.properties.table.actions.deactivate')}
        </Text>
      ),
      icon: <PiPauseCircle className="h-4 w-4 text-danger-700" />,
    },
  ];
};

export const buildPropertyCardViewModel = (
  property: CatalogProperty,
  fileUrlById: Map<string, string>,
  distanceLocale: Locale,
  onDeactivate: ((property: CatalogProperty) => void) | undefined,
  t: TranslateFn
): PropertyCardViewModel => {
  const primaryImageFileId = getPrimaryImageFileId(property);
  const imageUrl = primaryImageFileId
    ? fileUrlById.get(primaryImageFileId) ?? null
    : null;
  const salePrice = formatPriceValue(
    property.salePriceAmount,
    property.salePriceCurrency
  );
  const rentPrice = formatPriceValue(
    property.rentPriceAmount,
    property.rentPriceCurrency
  );
  const commonExpenses = formatPriceValue(
    property.commonExpensesAmount,
    property.commonExpensesCurrency
  );
  const locationSummary = buildLocationSummary(property);
  const address = buildAddress(property);
  const region = property.region?.trim() ? property.region : null;
  const hasPrice = salePrice != null || rentPrice != null;
  const hasLocation =
    locationSummary.length > 0 || address != null || region != null;
  const mapsUrl = buildGoogleMapsUrl(property);
  const mapsLabel =
    address ?? (locationSummary.length > 0 ? locationSummary : region);
  const syncProviderId = getSyncProviderId(property.sourceSystem);

  return {
    property,
    primaryImageFileId,
    imageUrl,
    salePrice,
    rentPrice,
    commonExpenses,
    locationSummary,
    address,
    region,
    mapsUrl,
    mapsLabel,
    metrics: buildPropertyMetrics(property, t),
    statusPills: buildStatusPills(property, syncProviderId, t),
    warnings: buildWarnings(property, hasPrice, hasLocation, t),
    updatedText: formatDistanceToNow(new Date(property.updatedAt), {
      addSuffix: true,
      locale: distanceLocale,
    }),
    userActions: buildUserActions(onDeactivate, property, t),
  };
};
